/**
 * One day, one column per clinician - the front desk's view of who is where. FullCalendar 3 has no
 * resource columns without the paid Scheduler plugin, so this draws them itself. It only lays out
 * what calendarController hands it (see buildTeamModel there); fetching, filtering and colouring all
 * stay in the controller, so the team view always agrees with the other views.
 */
app.component("teamDayView", {
	templateUrl: "app/src/js/components/teamDayView/teamDayView.html",
	controller: "teamDayViewController",
	bindings: {
		model: "<",
		canBook: "<",
		onOpen: "&",
		onPatient: "&",
		onSlot: "&",
		onMove: "&",
	},
});

app.controller("teamDayViewController", [
	"$scope",
	"$element",
	"$timeout",
	"$interval",
	function ($scope, $element, $timeout, $interval) {
		const $ctrl = this;

		// Must match the hour rule spacing in _calendar.css (.team-col background-size: 120px = 60 x 2).
		const PX_PER_MINUTE = 2;
		const SLOT_MINUTES = 10;
		// Appointment lengths are kept to steps of 5 minutes, so a move snaps to the same.
		const MOVE_STEP_MINUTES = 5;
		// How far the pointer travels before a press on an appointment is a drag rather than a click.
		const DRAG_THRESHOLD_PX = 4;
		const MIN_EVENT_HEIGHT = 20;

		$ctrl.columns = [];
		$ctrl.hours = [];
		$ctrl.bodyHeight = 0;
		$ctrl.nowTop = null;

		let scrolledForDay = null;
		let nowTimer = null;
		// The appointment being dragged, from mousedown until mouseup - see startDrag.
		let drag = null;
		// A drag ends with a click on whatever is under the pointer; that click must not open the appointment.
		let swallowClick = false;

		$ctrl.$onChanges = function (changes) {
			if (changes.model) {
				layout();
			}
		};

		$ctrl.$postLink = function () {
			// Bound outside Angular: a digest per mouse move would be wasted on a marker nobody binds to.
			$element.on("mousemove", ".team-col", function (event) {
				if (!$ctrl.canBook || $(event.target).closest(".team-appt").length) {
					hideGhost(this);
					return;
				}
				const minutes = minutesAt(event, this);
				const ghost = $(this).find(".team-ghost");
				ghost.css({ top: (minutes - $ctrl.model.startMinutes) * PX_PER_MINUTE + "px", display: "flex" });
				ghost.find(".team-ghost-time").text(formatMinutes(minutes));
			});
			$element.on("mouseleave", ".team-col", function () {
				hideGhost(this);
			});
			$element.on("mousedown", ".team-appt", startDrag);
		};

		$ctrl.$onDestroy = function () {
			$element.off("mousemove mouseleave mousedown");
			endDrag();
			if (nowTimer) {
				$interval.cancel(nowTimer);
			}
		};

		function hideGhost(column) {
			$(column).find(".team-ghost").css("display", "none");
		}

		function minutesAt(event, column) {
			const offset = event.clientY - column.getBoundingClientRect().top;
			const raw = $ctrl.model.startMinutes + offset / PX_PER_MINUTE;
			const snapped = Math.floor(raw / SLOT_MINUTES) * SLOT_MINUTES;
			return Math.max($ctrl.model.startMinutes, Math.min(snapped, $ctrl.model.endMinutes - SLOT_MINUTES));
		}

		/* =================================================================== */
		/* Drag to move
		/* Drag an appointment up or down its column to change the time, or across to another clinician's
		/* column to give it to them. Done by hand rather than with HTML5 drag and drop so the preview can
		/* snap to the grid and show the new time as it goes. Nothing is saved here - onMove hands the
		/* clinician and start time to calendarController, which saves and refetches.
		/* =================================================================== */

		function columnFor(element) {
			const column = $(element).closest(".team-col");
			if (!column.length) {
				return null;
			}
			const id = column.attr("data-col-id");
			return $ctrl.columns.some((c) => String(c.id) === id) ? { element: column[0], id: id } : null;
		}

		function findEvent(columnId, appointmentId) {
			const column = $ctrl.columns.find((c) => String(c.id) === String(columnId));
			return column ? column.events.find((ev) => String(ev.id) === String(appointmentId)) : null;
		}

		function startDrag(event) {
			// Left button only, and not from the patient link - that goes to the patient's dashboard.
			if (event.which !== 1 || !$ctrl.canBook || $(event.target).closest(".appt-patient-link").length) {
				return;
			}
			const column = columnFor(this);
			const ev = column && findEvent(column.id, $(this).attr("data-appt-id"));
			if (!ev) {
				return;
			}
			const columnTop = column.element.getBoundingClientRect().top;
			drag = {
				ev: ev,
				element: this,
				startX: event.clientX,
				startY: event.clientY,
				// Where on the appointment it was picked up, so it doesn't jump to put its top under the pointer.
				grabMinutes: (event.clientY - columnTop) / PX_PER_MINUTE + $ctrl.model.startMinutes - ev.startMin,
				duration: ev.endMin - ev.startMin,
				target: null,
				preview: null,
				moving: false,
			};
			$(document)
				.on("mousemove.teamDrag", moveDrag)
				.on("mouseup.teamDrag", dropDrag)
				.on("keydown.teamDrag", function (e) {
					if (e.key === "Escape") {
						endDrag();
					}
				});
			// Stops the browser selecting text across the grid while dragging.
			event.preventDefault();
		}

		function moveDrag(event) {
			if (!drag) {
				return;
			}
			if (!drag.moving) {
				if (Math.abs(event.clientX - drag.startX) < DRAG_THRESHOLD_PX && Math.abs(event.clientY - drag.startY) < DRAG_THRESHOLD_PX) {
					return;
				}
				drag.moving = true;
				$(drag.element).addClass("is-drag-source");
				$element.find(".team-day").addClass("is-dragging");
				drag.preview = $('<div class="team-drag-preview"><span class="team-drag-time"></span><span class="team-drag-title"></span></div>');
				drag.preview.find(".team-drag-title").text(drag.ev.title || "");
				drag.preview.css({
					height: Math.max(drag.duration * PX_PER_MINUTE - 2, MIN_EVENT_HEIGHT) + "px",
					"border-left-color": drag.ev.colour,
				});
			}

			// The column under the pointer; off the grid, the preview stays where it last was.
			const column = columnFor(document.elementFromPoint(event.clientX, event.clientY));
			if (!column) {
				return;
			}
			const raw = (event.clientY - column.element.getBoundingClientRect().top) / PX_PER_MINUTE + $ctrl.model.startMinutes - drag.grabMinutes;
			const snapped = Math.round(raw / MOVE_STEP_MINUTES) * MOVE_STEP_MINUTES;
			const latest = Math.max($ctrl.model.startMinutes, $ctrl.model.endMinutes - drag.duration);
			const startMin = Math.max($ctrl.model.startMinutes, Math.min(snapped, latest));

			drag.target = { columnId: column.id, startMin: startMin };
			if (drag.preview.parent()[0] !== column.element) {
				drag.preview.appendTo(column.element);
			}
			drag.preview.css("top", (startMin - $ctrl.model.startMinutes) * PX_PER_MINUTE + "px");
			drag.preview.find(".team-drag-time").text(formatMinutes(startMin) + " - " + formatMinutes(startMin + drag.duration));
			hideGhost(column.element);
		}

		function dropDrag() {
			if (!drag) {
				return;
			}
			const current = drag;
			endDrag();
			if (!current.moving) {
				return; // A plain click - the appointment's own ng-click opens it.
			}
			swallowClick = true;
			// The click that follows mouseup fires straight after it; anything later is a new click.
			$timeout(() => (swallowClick = false), 0, false);

			const target = current.target;
			if (!target || (String(target.columnId) === String(current.ev.column) && target.startMin === current.ev.startMin)) {
				return;
			}
			const column = $ctrl.columns.find((c) => String(c.id) === String(target.columnId));
			$scope.$apply(function () {
				$ctrl.onMove({
					appointmentId: current.ev.id,
					clinicianId: column ? column.id : target.columnId,
					startTime: `${pad(Math.floor(target.startMin / 60))}:${pad(target.startMin % 60)}`,
				});
			});
		}

		function endDrag() {
			$(document).off(".teamDrag");
			if (!drag) {
				return;
			}
			if (drag.preview) {
				drag.preview.remove();
			}
			$(drag.element).removeClass("is-drag-source");
			$element.find(".team-day").removeClass("is-dragging");
			drag = null;
		}

		function formatMinutes(minutes) {
			return moment().startOf("day").add(minutes, "minutes").locale("en").format("h:mm a");
		}

		function pad(value) {
			return (value < 10 ? "0" : "") + value;
		}

		function layout() {
			const model = $ctrl.model;
			if (!model) {
				$ctrl.columns = [];
				return;
			}

			$ctrl.bodyHeight = (model.endMinutes - model.startMinutes) * PX_PER_MINUTE;
			$ctrl.hours = [];
			for (let m = Math.ceil(model.startMinutes / 60) * 60; m < model.endMinutes; m += 60) {
				$ctrl.hours.push({
					top: (m - model.startMinutes) * PX_PER_MINUTE,
					label: moment().startOf("day").add(m, "minutes").locale("en").format("h a"),
				});
			}

			$ctrl.columns = model.columns.map((column) => ({
				id: column.id,
				name: column.name,
				colour: column.colour,
				count: column.count,
				events: layoutColumn(column.events, model, column.id),
				away: layoutAway(column.away || [], model),
				sessions: layoutAway(column.sessions || [], model),
				awayAllDay: column.awayAllDay,
				awayShort: column.awayShort,
			}));

			updateNow();
			if (nowTimer) {
				$interval.cancel(nowTimer);
				nowTimer = null;
			}
			if (model.isToday) {
				nowTimer = $interval(updateNow, 60 * 1000);
			}

			// Open each day at the working morning (or an hour before now, today) rather than at 7am. Only
			// once there are columns - the scroller is not drawn without them.
			if (scrolledForDay !== model.day && $ctrl.columns.length > 0) {
				$timeout(function () {
					const scroller = $element[0].querySelector(".team-day-scroller");
					if (!scroller) {
						return;
					}
					scrolledForDay = model.day;
					const nowMinutes = model.isToday ? moment().tz(model.timezone).hours() * 60 : 0;
					const target = Math.max(8 * 60, nowMinutes - 60);
					scroller.scrollTop = Math.max(0, (target - model.startMinutes) * PX_PER_MINUTE);
				});
			}
		}

		/**
		 * Positions one clinician's appointments. Overlapping ones (double bookings, a short check slotted
		 * inside a long procedure) are set side by side across the column, each cluster of overlaps
		 * sharing the width between however many lanes it needs.
		 */
		function layoutColumn(events, model, columnId) {
			const items = events
				.map((ev) => {
					const start = ev.start.hours() * 60 + ev.start.minutes();
					const duration = Math.max(ev.end.diff(ev.start, "minutes"), 5);
					return angular.extend({}, ev, { startMin: start, endMin: start + duration, column: columnId });
				})
				.sort((a, b) => a.startMin - b.startMin || b.endMin - a.endMin);

			const clusters = [];
			let cluster = [];
			let clusterEnd = -1;
			items.forEach((item) => {
				if (cluster.length > 0 && item.startMin >= clusterEnd) {
					clusters.push(cluster);
					cluster = [];
					clusterEnd = -1;
				}
				cluster.push(item);
				clusterEnd = Math.max(clusterEnd, item.endMin);
			});
			if (cluster.length > 0) {
				clusters.push(cluster);
			}

			clusters.forEach((group) => {
				const laneEnds = [];
				group.forEach((item) => {
					let lane = laneEnds.findIndex((end) => end <= item.startMin);
					if (lane === -1) {
						lane = laneEnds.length;
						laneEnds.push(item.endMin);
					} else {
						laneEnds[lane] = item.endMin;
					}
					item.lane = lane;
				});
				group.forEach((item) => (item.lanes = laneEnds.length));
			});

			return items.map((item) => {
				const visibleStart = Math.min(Math.max(item.startMin, model.startMinutes), model.endMinutes - SLOT_MINUTES);
				const visibleEnd = Math.min(Math.max(item.endMin, visibleStart + 5), model.endMinutes);
				const height = Math.max((visibleEnd - visibleStart) * PX_PER_MINUTE - 2, MIN_EVENT_HEIGHT);
				return angular.extend(item, {
					top: (visibleStart - model.startMinutes) * PX_PER_MINUTE,
					height: height,
					left: (item.lane / item.lanes) * 100 + "%",
					width: `calc(${100 / item.lanes}% - 4px)`,
					isShort: height < 40,
					startLabel: item.start.locale("en").format("h:mm"),
					endLabel: item.end.locale("en").format("h:mm a"),
				});
			});
		}

		// Time away and sessions, clipped to the hours drawn. Whole days fill the column.
		function layoutAway(blocks, model) {
			return blocks
				.map((block) => {
					const start = Math.max(block.startMin, model.startMinutes);
					const end = Math.min(block.endMin, model.endMinutes);
					return angular.extend({}, block, {
						top: (start - model.startMinutes) * PX_PER_MINUTE,
						height: (end - start) * PX_PER_MINUTE,
						// A session's name sits in its first free stretch (labelOffset, minutes from its start).
						labelTop: block.labelOffset === null || block.labelOffset === undefined
							? null
							: (block.startMin + block.labelOffset - start) * PX_PER_MINUTE,
					});
				})
				.filter((block) => block.height > 0);
		}

		function updateNow() {
			const model = $ctrl.model;
			if (!model || !model.isToday) {
				$ctrl.nowTop = null;
				return;
			}
			const now = moment().tz(model.timezone);
			const minutes = now.hours() * 60 + now.minutes();
			$ctrl.nowTop = minutes >= model.startMinutes && minutes <= model.endMinutes
				? (minutes - model.startMinutes) * PX_PER_MINUTE
				: null;
		}

		$ctrl.columnClick = function (event, column) {
			if (swallowClick) {
				return;
			}
			const minutes = minutesAt(event, event.currentTarget);
			$ctrl.onSlot({
				clinicianId: column.id,
				startTime: `${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}`,
				durationMinutes: SLOT_MINUTES,
			});
		};

		$ctrl.open = function (event, ev) {
			event.stopPropagation();
			if (swallowClick) {
				return;
			}
			$ctrl.onOpen({ appointmentId: ev.id });
		};

		$ctrl.patient = function (event, ev) {
			event.preventDefault();
			event.stopPropagation();
			$ctrl.onPatient({ appointment: ev.appointment });
		};
	},
]);
